import { Authenticated, CanAccess, Refine } from '@refinedev/core'
import routerProvider, {
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router'
import { Gem } from 'lucide-react'
import type { ReactNode } from 'react'
import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router'
import { createAdminClient } from '~/api/adminClient'
import { AdminClientProvider } from '~/api/adminClientContext'
import { createAdminAuthClient } from '~/auth/authClient'
import { PlatformOnly } from '~/components/platformOnly'
import { Layout } from '~/components/refineUi/layout/layout'
import { Toaster } from '~/components/refineUi/notification/toaster'
import { useNotificationProvider } from '~/components/refineUi/notification/useNotificationProvider'
import { ThemeProvider } from '~/components/refineUi/theme/themeProvider'
import { CabinetUrlProvider } from '~/config/cabinetUrlContext'
import type { Env } from '~/config/env'
import { createI18n } from '~/i18n/createI18n'
import { bindErrorTexts } from '~/i18n/errorText'
import { PanelLanguageProvider } from '~/i18n/panelLanguage'
import { createI18nProvider } from '~/i18n/refineProvider'
import { installZodMessages } from '~/i18n/zodMessages'
import { documentTitle } from '~/lib/documentTitle'
import { AcceptInvitationPage } from '~/pages/acceptInvitation/acceptInvitationPage'
import { ActivityList, PlatformActivityList } from '~/pages/activity/list'
import { CabinetCreate } from '~/pages/cabinets/create'
import { CabinetEdit } from '~/pages/cabinets/edit'
import { CabinetList } from '~/pages/cabinets/list'
import { CabinetShow } from '~/pages/cabinets/show'
import { DashboardPage } from '~/pages/dashboard/dashboardPage'
import { ForgotPasswordPage } from '~/pages/forgotPassword'
import { GameList } from '~/pages/games/list'
import { GameShow } from '~/pages/games/show'
import { LocationCreate } from '~/pages/locations/create'
import { LocationEdit } from '~/pages/locations/edit'
import { LocationList } from '~/pages/locations/list'
import { LocationShow } from '~/pages/locations/show'
import { LoginPage } from '~/pages/login'
import { NotFoundPage } from '~/pages/notFound'
import { PlatformSettingsPage } from '~/pages/platformSettings/platformSettingsPage'
import { ResetPasswordPage } from '~/pages/resetPassword'
import { RoundShow } from '~/pages/rounds/show'
import { SettingsPage } from '~/pages/settings/settingsPage'
import { TenantCreate } from '~/pages/tenants/create'
import { TenantEdit } from '~/pages/tenants/edit'
import { TenantList } from '~/pages/tenants/list'
import { TenantShow } from '~/pages/tenants/show'
import { UserList } from '~/pages/users/list'
import { UserShow } from '~/pages/users/show'
import {
  createAccessControlProvider,
  DASHBOARD_RESOURCE,
  SETTINGS_RESOURCE,
} from '~/providers/accessControl'
import {
  ACCEPT_INVITATION_PATH,
  createAuthProvider,
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  loginRedirectPath,
  RESET_PASSWORD_PATH,
} from '~/providers/authProvider'
import { createDataProvider } from '~/providers/dataProvider'
import { createQueryClient } from '~/providers/queryClient'
import {
  createMeLoader,
  withSessionQueryReset,
} from '~/providers/sessionQueries'
import { resources } from '~/resources'
import { BaseDomainProvider, tenantRef } from '~/tenancy/activeTenant'
import { hostSlugRef, hostTenantSlug } from '~/tenancy/hostSlug'
import { TenantGate } from '~/tenancy/tenantGate'
import { TenantSwitcher } from '~/tenancy/tenantSwitcher'

// a configuration's page brings CodeMirror (its Raw config tab): loaded
// when a user opens one
const GameConfigurationShow = lazy(async () => {
  const { GameConfigurationShow: page } =
    await import('~/pages/games/configurationShow')
  return { default: page }
})

/** What a configuration page shows while its code loads. */
function ConfigurationFallback() {
  const { t } = useTranslation()
  return (
    <p className="text-muted-foreground text-sm">
      {t('shell.loadingConfiguration')}
    </p>
  )
}
const configurationFallback = <ConfigurationFallback />

/**
 * Sends signed-out users to the login page with `?returnTo=`. Not Refine's
 * `CatchAllNavigate`: it appends `?to=<raw path>`, which `useLogin` follows
 * unchecked after sign-in.
 */
function NavigateToLogin() {
  return (
    <Navigate
      replace
      to={loginRedirectPath(useLocation())}
    />
  )
}

/** Index and CRUD routes of one resource; screens default to placeholders. */
function crudRoutes(
  path: string,
  pages: {
    list?: ReactNode
    create?: ReactNode
    show?: ReactNode
    edit?: ReactNode
  } = {},
) {
  return (
    <Route path={path}>
      <Route
        index
        element={pages.list ?? <NotFoundPage />}
      />
      <Route
        path="new"
        element={pages.create ?? <NotFoundPage />}
      />
      <Route
        path=":id"
        element={pages.show ?? <NotFoundPage />}
      />
      <Route
        path=":id/edit"
        element={pages.edit ?? <NotFoundPage />}
      />
    </Route>
  )
}

export function createApp(env: Env) {
  const apiUrl = env.VITE_ADMIN_API_URL
  const baseDomain = env.VITE_ADMIN_BASE_DOMAIN
  const cabinetUrl = env.VITE_ADMIN_CABINET_URL
  const client = createAdminClient(apiUrl)
  // the tenant whose host this page runs on: the session names it, so the
  // panel language can follow the tenant's default
  const tenantSlug = hostTenantSlug(window.location.hostname, baseDomain)
  hostSlugRef.current = tenantSlug
  const i18n = createI18n()
  bindErrorTexts(i18n)
  installZodMessages()
  const i18nProvider = createI18nProvider(i18n)
  const authClient = createAdminAuthClient(apiUrl)
  // shared with Refine, so oRPC's TanStack Query utils (`createAdminQueryUtils`)
  // use the same cache in custom screens
  const queryClient = createQueryClient()
  const getMe = createMeLoader(client, queryClient)
  const dataProvider = createDataProvider({
    client,
    apiUrl,
    getTenantId: () => tenantRef.current,
    isPlatformAdmin: async () => (await getMe()).platformRole === 'admin',
  })
  const authProvider = withSessionQueryReset(
    createAuthProvider({ authClient, client, getMe }),
    queryClient,
  )
  const accessControlProvider = createAccessControlProvider({
    getMe,
    getTenantId: () => tenantRef.current,
  })

  return function App() {
    return (
      <BrowserRouter>
        <ThemeProvider>
          <AdminClientProvider client={client}>
            <PanelLanguageProvider
              i18n={i18n}
              client={client}
              queryClient={queryClient}
              tenantSlug={tenantSlug}
            >
              <BaseDomainProvider baseDomain={baseDomain}>
                <CabinetUrlProvider url={cabinetUrl}>
                  <Refine
                    dataProvider={dataProvider}
                    authProvider={authProvider}
                    accessControlProvider={accessControlProvider}
                    i18nProvider={i18nProvider}
                    routerProvider={routerProvider}
                    notificationProvider={useNotificationProvider}
                    resources={resources}
                    options={{
                      title: { text: 'Sirius Admin', icon: <Gem /> },
                      reactQuery: { clientConfig: queryClient },
                      syncWithLocation: true,
                      warnWhenUnsavedChanges: true,
                      disableTelemetry: true,
                    }}
                  >
                    <Routes>
                      <Route
                        element={
                          <Authenticated
                            key="authenticated"
                            fallback={<NavigateToLogin />}
                          >
                            <TenantGate
                              client={client}
                              authClient={authClient}
                              baseDomain={baseDomain}
                            >
                              <Layout
                                headerStart={
                                  <TenantSwitcher
                                    client={client}
                                    baseDomain={baseDomain}
                                  />
                                }
                              >
                                <Outlet />
                              </Layout>
                            </TenantGate>
                          </Authenticated>
                        }
                      >
                        <Route
                          index
                          element={
                            // where every sign-in lands: members (no
                            // dashboard) go on to the locations
                            <CanAccess
                              resource={DASHBOARD_RESOURCE}
                              action="list"
                              fallback={
                                <NavigateToResource resource="locations" />
                              }
                            >
                              <DashboardPage />
                            </CanAccess>
                          }
                        />
                        {crudRoutes('/locations', {
                          list: <LocationList />,
                          // a technician only reads locations
                          create: (
                            <CanAccess
                              resource="locations"
                              action="create"
                              fallback={<NotFoundPage />}
                            >
                              <LocationCreate />
                            </CanAccess>
                          ),
                          show: <LocationShow />,
                          edit: (
                            <CanAccess
                              resource="locations"
                              action="edit"
                              fallback={<NotFoundPage />}
                            >
                              <LocationEdit />
                            </CanAccess>
                          ),
                        })}
                        {crudRoutes('/cabinets', {
                          list: <CabinetList />,
                          create: <CabinetCreate />,
                          show: <CabinetShow />,
                          edit: <CabinetEdit />,
                        })}
                        <Route
                          path="/cabinets/:cabinetId/rounds/:id"
                          element={
                            // not-found (not 403) for members, like the log
                            <CanAccess
                              resource="rounds"
                              action="show"
                              fallback={<NotFoundPage />}
                            >
                              <RoundShow />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="/activity"
                          element={
                            // not-found (not 403) for members, like StaffOnly
                            <CanAccess
                              resource="activity"
                              action="list"
                              fallback={<NotFoundPage />}
                            >
                              <ActivityList />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            // not-found (not 403) for members, like the log
                            <CanAccess
                              resource={SETTINGS_RESOURCE}
                              action="list"
                              fallback={<NotFoundPage />}
                            >
                              <SettingsPage />
                            </CanAccess>
                          }
                        />
                        <Route
                          path="/platform"
                          element={
                            // a technician reads the tenants, nothing else here
                            <PlatformOnly allow={['admin', 'technician']}>
                              <Outlet />
                            </PlatformOnly>
                          }
                        >
                          {crudRoutes('tenants', {
                            list: <TenantList />,
                            create: (
                              <PlatformOnly>
                                <TenantCreate />
                              </PlatformOnly>
                            ),
                            show: <TenantShow />,
                            edit: (
                              <PlatformOnly>
                                <TenantEdit />
                              </PlatformOnly>
                            ),
                          })}
                          <Route
                            element={
                              <PlatformOnly>
                                <Outlet />
                              </PlatformOnly>
                            }
                          >
                            {crudRoutes('users', {
                              list: <UserList />,
                              show: <UserShow />,
                            })}
                            {crudRoutes('games', {
                              list: <GameList />,
                              show: <GameShow />,
                            })}
                            <Route
                              path="activity"
                              element={<PlatformActivityList />}
                            />
                            {/* the platform's defaults (one record) */}
                            <Route
                              path="settings"
                              element={<PlatformSettingsPage />}
                            />
                            {/* a configuration: its editor, save history,
                            simulations and test seeds */}
                            <Route
                              path="games/:id/configurations/:configurationId"
                              element={
                                <Suspense fallback={configurationFallback}>
                                  <GameConfigurationShow />
                                </Suspense>
                              }
                            />
                          </Route>
                        </Route>
                        <Route
                          path="*"
                          element={<NotFoundPage />}
                        />
                      </Route>
                      <Route
                        element={
                          <Authenticated
                            key="anonymous"
                            fallback={<Outlet />}
                          >
                            <NavigateToResource />
                          </Authenticated>
                        }
                      >
                        <Route
                          path={LOGIN_PATH}
                          element={<LoginPage />}
                        />
                        <Route
                          path={FORGOT_PASSWORD_PATH}
                          element={<ForgotPasswordPage />}
                        />
                      </Route>
                      {/* the mailed reset link works signed in or out: whoever
                      holds it may set the account's password */}
                      <Route
                        path={RESET_PASSWORD_PATH}
                        element={<ResetPasswordPage />}
                      />
                      {/* an invitation's mailed link, signed in or out and
                      outside the tenant gate: the invitee may be a member
                      of no tenant yet */}
                      <Route
                        path={`${ACCEPT_INVITATION_PATH}/:id`}
                        element={
                          <AcceptInvitationPage authClient={authClient} />
                        }
                      />
                    </Routes>
                    <Toaster />
                    <UnsavedChangesNotifier />
                    <DocumentTitleHandler handler={documentTitle} />
                  </Refine>
                </CabinetUrlProvider>
              </BaseDomainProvider>
            </PanelLanguageProvider>
          </AdminClientProvider>
        </ThemeProvider>
      </BrowserRouter>
    )
  }
}
